import { useFrame } from '@react-three/fiber'
import { useMemo, useRef } from 'react'
import * as THREE from 'three'
import { useEmissor } from '../ambiente/registroPosProcessamento'

export interface FaiscasChoqueEletrico3DProps {
  /** Posição base do ponto de contato/arco elétrico [x, y, z] */
  posicao?: [number, number, number]
  /** Fator de escala geral dos efeitos visuais */
  escala?: number
  /** Se o efeito está energizado/emitindo faíscas */
  ativo?: boolean
}

/**
 * Efeito visual 3D procedural de Faíscas de Choque Elétrico e Arcos Voltaicos de Alta Tensão.
 *
 * Utilizado especificamente no fio do poste e cabos de rede elétrica caídos:
 * - Arcos voltaicos em zigue-zague (azul ciano elétrico e plasma branco) pulsando em alta frequência
 * - Centelhas e fagulhas que saltam radialmente do ponto de contato
 * - Estrobo ciano de alta voltagem iluminando o entorno
 * - Ponto de fusão de cobre incandescente
 */
export function FaiscasChoqueEletrico3D({
  posicao = [0, 0, 0],
  escala = 1.0,
  ativo = true,
}: FaiscasChoqueEletrico3DProps) {
  useEmissor(ativo)
  const arcosRef = useRef<THREE.Group>(null)
  const faiscasRef = useRef<THREE.Group>(null)
  const luzChoqueRef = useRef<THREE.PointLight>(null)

  // Sementes aleatórias para trajetórias de faíscas de choque
  const dadosFaiscas = useMemo(
    () =>
      Array.from({ length: 8 }, (_, i) => ({
        id: i,
        ang: (i / 8) * Math.PI * 2 + (Math.random() - 0.5) * 0.4,
        raioBase: 0.12 + Math.random() * 0.22,
        alturaBase: 0.08 + Math.random() * 0.25,
        frequencia: 18 + i * 4,
        fase: Math.random() * Math.PI * 2,
      })),
    [],
  )

  useFrame(({ clock }) => {
    if (!ativo) return
    const t = clock.elapsedTime

    // 1. Arcos Elétricos e Faíscas de Choque (Alta Frequência / Estocástico)
    const emDisparoChoque = Math.sin(t * 37) > 0.12 || Math.random() > 0.62

    if (arcosRef.current) {
      arcosRef.current.visible = emDisparoChoque
      if (emDisparoChoque) {
        arcosRef.current.rotation.y = Math.random() * Math.PI * 2
        arcosRef.current.scale.set(
          0.85 + Math.random() * 0.5,
          0.85 + Math.random() * 0.6,
          0.85 + Math.random() * 0.5,
        )
      }
    }

    // Estrobo azul-ciano intenso do clarão elétrico
    if (luzChoqueRef.current) {
      if (emDisparoChoque && Math.random() > 0.25) {
        luzChoqueRef.current.intensity = (3.2 + Math.random() * 3.5) * escala
      } else {
        luzChoqueRef.current.intensity = 0
      }
    }

    // 2. Centelhas saltando radialmente
    if (faiscasRef.current) {
      faiscasRef.current.children.forEach((child, idx) => {
        const dado = dadosFaiscas[idx]
        if (!dado) return
        const ciclo = (t * (dado.frequencia * 0.1) + dado.fase) % 1
        const dR = dado.raioBase * (0.3 + ciclo * 1.8)
        const dY = dado.alturaBase * Math.sin(ciclo * Math.PI)
        child.position.set(Math.cos(dado.ang) * dR, dY, Math.sin(dado.ang) * dR)
        child.scale.setScalar((1 - ciclo) * 1.3)
      })
    }
  })

  if (!ativo) return null

  return (
    <group position={posicao} scale={escala} aria-label="Faíscas de choque elétrico">
      {/* Estrobo ciano de alta voltagem */}
      <pointLight ref={luzChoqueRef} color="#38bdf8" distance={6.0} decay={2} intensity={0} />

      {/* Marca de queima pontual do arco no solo */}
      <mesh position={[0, 0.005, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <circleGeometry args={[0.18, 12]} />
        <meshStandardMaterial color="#090d16" roughness={1.0} />
      </mesh>

      {/* Ponto de fusão de cobre desencapado */}
      <mesh position={[0, 0.02, 0]}>
        <sphereGeometry args={[0.035, 8, 8]} />
        <meshStandardMaterial color="#f59e0b" emissive="#f59e0b" emissiveIntensity={3.5} />
      </mesh>

      {/* Arcos voltaicos elétricos em zigue-zague */}
      <group ref={arcosRef} position={[0, 0.04, 0]}>
        {/* Arco 1 (Azul ciano elétrico) */}
        <mesh position={[-0.05, 0.12, 0.04]} rotation={[0.4, -0.6, 0.7]}>
          <cylinderGeometry args={[0.014, 0.014, 0.28, 5]} />
          <meshStandardMaterial
            color="#38bdf8"
            emissive="#0284c7"
            emissiveIntensity={6.0}
            roughness={0.1}
          />
        </mesh>
        {/* Arco 2 (Plasma branco puro) */}
        <mesh position={[0.06, 0.16, -0.05]} rotation={[-0.5, 0.8, -0.6]}>
          <cylinderGeometry args={[0.011, 0.018, 0.32, 5]} />
          <meshStandardMaterial
            color="#f0f9ff"
            emissive="#e0f2fe"
            emissiveIntensity={7.0}
            roughness={0.1}
          />
        </mesh>
        {/* Arco 3 (Ramificação de choque) */}
        <mesh position={[0.1, 0.08, 0.07]} rotation={[0.8, 0.3, 1.1]}>
          <cylinderGeometry args={[0.01, 0.01, 0.2, 4]} />
          <meshStandardMaterial
            color="#7dd3fc"
            emissive="#38bdf8"
            emissiveIntensity={5.5}
            roughness={0.1}
          />
        </mesh>
        {/* Núcleo central da descarga elétrica */}
        <mesh position={[0, 0.07, 0]}>
          <sphereGeometry args={[0.045, 8, 8]} />
          <meshStandardMaterial
            color="#ffffff"
            emissive="#38bdf8"
            emissiveIntensity={7.0}
          />
        </mesh>
      </group>

      {/* Centelhas elétricas expelidas radialmente */}
      <group ref={faiscasRef} position={[0, 0.06, 0]}>
        {dadosFaiscas.map((f) => (
          <mesh key={f.id} position={[0, 0, 0]}>
            <sphereGeometry args={[0.02, 5, 5]} />
            <meshStandardMaterial
              color={f.id % 2 === 0 ? '#38bdf8' : '#fef08a'}
              emissive={f.id % 2 === 0 ? '#0284c7' : '#eab308'}
              emissiveIntensity={5.5}
            />
          </mesh>
        ))}
      </group>
    </group>
  )
}

export interface Fogo3DProps {
  /** Posição base do fogo [x, y, z] */
  posicao?: [number, number, number]
  /** Fator de escala geral das chamas */
  escala?: number
  /** Se o fogo está ativo ou extinto */
  ativo?: boolean
  /** Se deve exibir coluna de fumaça */
  comFumaca?: boolean
}

/**
 * Efeito visual 3D procedural de Fogo / Princípio de Incêndio.
 *
 * Elemento tático independente para ser colocado no carro, compartimento do motor,
 * solo ou em qualquer área da ocorrência:
 * - Chamas volumosas multicamadas com línguas de fogo animadas em turbulência
 * - Coluna de fumaça cinza-escura subindo por convecção
 * - Brasas e fagulhas incandescentes ascendentes
 * - Iluminação quente pulsante que projeta luz sobre veículos e solo
 * - Mancha de asfalto chamuscado com brasas (ou cinzas apagadas quando extinto)
 */
export function Fogo3D({
  posicao = [0, 0, 0],
  escala = 1.0,
  ativo = true,
  comFumaca = true,
}: Fogo3DProps) {
  useEmissor(ativo)
  const grupoChamasRef = useRef<THREE.Group>(null)
  const labaredaCentralRef = useRef<THREE.Mesh>(null)
  const labaredaLaranjaRef = useRef<THREE.Mesh>(null)
  const labaredaAmarelaRef = useRef<THREE.Mesh>(null)

  const labareda1Ref = useRef<THREE.Mesh>(null)
  const labareda2Ref = useRef<THREE.Mesh>(null)
  const labareda3Ref = useRef<THREE.Mesh>(null)
  const labareda4Ref = useRef<THREE.Mesh>(null)

  const fumacaGrupoRef = useRef<THREE.Group>(null)
  const brasasGrupoRef = useRef<THREE.Group>(null)
  const luzFogoRef = useRef<THREE.PointLight>(null)

  // Sementes para tufos de fumaça ascendente
  const dadosFumaca = useMemo(
    () =>
      Array.from({ length: 5 }, (_, i) => ({
        id: i,
        offsetY: 0.25 + i * 0.35,
        velocidade: 0.6 + i * 0.15,
        raio: 0.14 + i * 0.07,
        faseX: Math.random() * Math.PI * 2,
        faseZ: Math.random() * Math.PI * 2,
      })),
    [],
  )

  // Sementes para brasas incandescentes subindo com o calor
  const dadosBrasas = useMemo(
    () =>
      Array.from({ length: 6 }, (_, i) => ({
        id: i,
        ang: (i / 6) * Math.PI * 2,
        raio: 0.06 + Math.random() * 0.12,
        velocidade: 0.9 + Math.random() * 0.6,
        alturaMax: 0.7 + Math.random() * 0.5,
        fase: i / 6,
      })),
    [],
  )

  useFrame(({ clock }) => {
    if (!ativo) return
    const t = clock.elapsedTime

    // 1. Turbulência e Dança das Chamas de Fogo
    if (grupoChamasRef.current) {
      const escalaY = 1 + 0.32 * Math.sin(t * 22) + 0.16 * Math.sin(t * 37)
      const escalaXZ = 1 + 0.18 * Math.cos(t * 26)
      grupoChamasRef.current.scale.set(escalaXZ, escalaY, escalaXZ)
      grupoChamasRef.current.rotation.z = Math.sin(t * 16) * 0.14
      grupoChamasRef.current.rotation.x = Math.cos(t * 13) * 0.11
    }

    if (labaredaCentralRef.current) {
      labaredaCentralRef.current.rotation.y = t * 5
    }
    if (labaredaLaranjaRef.current) {
      labaredaLaranjaRef.current.rotation.y = -t * 7
      const pulso = 1 + 0.25 * Math.sin(t * 29)
      labaredaLaranjaRef.current.scale.set(pulso, pulso * 1.1, pulso)
    }
    if (labaredaAmarelaRef.current) {
      const pulsoNucleo = 1 + 0.22 * Math.sin(t * 33)
      labaredaAmarelaRef.current.scale.set(pulsoNucleo, pulsoNucleo, pulsoNucleo)
    }

    // Línguas de fogo individuais (flame tongues)
    if (labareda1Ref.current) {
      labareda1Ref.current.scale.y = 1 + 0.45 * Math.sin(t * 21 + 0.5)
      labareda1Ref.current.rotation.z = 0.3 + 0.15 * Math.sin(t * 15)
    }
    if (labareda2Ref.current) {
      labareda2Ref.current.scale.y = 1 + 0.4 * Math.sin(t * 27 + 1.2)
      labareda2Ref.current.rotation.x = -0.28 + 0.12 * Math.cos(t * 18)
    }
    if (labareda3Ref.current) {
      labareda3Ref.current.scale.y = 1 + 0.5 * Math.sin(t * 24 + 2.1)
      labareda3Ref.current.rotation.z = -0.32 + 0.14 * Math.cos(t * 16)
    }
    if (labareda4Ref.current) {
      labareda4Ref.current.scale.y = 1 + 0.38 * Math.sin(t * 31 + 3.4)
      labareda4Ref.current.rotation.x = 0.25 + 0.15 * Math.sin(t * 20)
    }

    // 2. Coluna de Fumaça Volumosa Ascendente
    if (fumacaGrupoRef.current) {
      fumacaGrupoRef.current.children.forEach((child, idx) => {
        const dado = dadosFumaca[idx]
        if (!dado) return
        const progresso = ((t * dado.velocidade + dado.id * 0.3) % 1.5) / 1.5
        const y = 0.2 + progresso * 1.6
        const derivaX = Math.sin(t * 2.5 + dado.faseX) * 0.12 * (1 + progresso)
        const derivaZ = Math.cos(t * 2.0 + dado.faseZ) * 0.12 * (1 + progresso)
        child.position.set(derivaX, y, derivaZ)
        const escalaFumaca = (1 + progresso * 2.2) * (1 - progresso * 0.35)
        child.scale.setScalar(escalaFumaca)
      })
    }

    // 3. Brasas Incandescentes Subindo no Ar Quente
    if (brasasGrupoRef.current) {
      brasasGrupoRef.current.children.forEach((child, idx) => {
        const dado = dadosBrasas[idx]
        if (!dado) return
        const progresso = (t * dado.velocidade + dado.fase) % 1
        const y = progresso * dado.alturaMax
        const ang = dado.ang + t * 4
        const r = dado.raio * (0.8 + progresso * 0.8)
        child.position.set(Math.cos(ang) * r, y, Math.sin(ang) * r)
        child.scale.setScalar((1 - progresso) * 1.2)
      })
    }

    // 4. Luz Quente do Fogo
    if (luzFogoRef.current) {
      const flicker = 0.85 + 0.4 * Math.sin(t * 19) + 0.25 * Math.sin(t * 31) + 0.2 * Math.random()
      luzFogoRef.current.intensity = flicker * 2.4 * escala
    }
  })

  if (!ativo) {
    // Fogo extinto / inativo: exibe apenas o solo chamuscado com cinzas apagadas
    return (
      <group position={posicao} scale={escala} aria-label="Foco de incêndio extinto">
        <mesh position={[0, 0.005, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <circleGeometry args={[0.3, 16]} />
          <meshStandardMaterial color="#0f172a" roughness={0.98} />
        </mesh>
      </group>
    )
  }

  return (
    <group position={posicao} scale={escala} aria-label="Fogo ativo / Princípio de incêndio">
      {/* Luz Quente Incandescente */}
      <pointLight ref={luzFogoRef} color="#f97316" distance={6.0} decay={2} intensity={2.2} />

      {/* Solo Chamuscado e Brasas */}
      <group>
        <mesh position={[0, 0.005, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <circleGeometry args={[0.38, 16]} />
          <meshStandardMaterial color="#090d16" roughness={1.0} />
        </mesh>
        <mesh position={[0, 0.008, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <circleGeometry args={[0.18, 14]} />
          <meshStandardMaterial
            color="#ef4444"
            emissive="#dc2626"
            emissiveIntensity={3.0}
            roughness={0.5}
          />
        </mesh>
      </group>

      {/* Chamas de Fogo Procedurais Vibrantes (Línguas de Fogo Multicamadas) */}
      <group ref={grupoChamasRef} position={[0, 0.02, 0]}>
        {/* Labareda central vermelha */}
        <mesh ref={labaredaCentralRef} position={[0, 0.32, 0]}>
          <coneGeometry args={[0.22, 0.65, 8]} />
          <meshStandardMaterial
            color="#dc2626"
            emissive="#b91c1c"
            emissiveIntensity={2.8}
            transparent
            opacity={0.88}
            roughness={0.3}
          />
        </mesh>

        {/* Chamas intermediárias laranja fogo */}
        <mesh ref={labaredaLaranjaRef} position={[0, 0.26, 0]}>
          <coneGeometry args={[0.16, 0.52, 8]} />
          <meshStandardMaterial
            color="#f97316"
            emissive="#ea580c"
            emissiveIntensity={3.8}
            transparent
            opacity={0.92}
            roughness={0.2}
          />
        </mesh>

        {/* Núcleo interno de alta combustão: amarelo incandescente */}
        <mesh ref={labaredaAmarelaRef} position={[0, 0.18, 0]}>
          <coneGeometry args={[0.095, 0.36, 7]} />
          <meshStandardMaterial
            color="#fef08a"
            emissive="#fde047"
            emissiveIntensity={5.0}
            roughness={0.1}
          />
        </mesh>

        {/* Raiz de plasma branco puro */}
        <mesh position={[0, 0.08, 0]}>
          <sphereGeometry args={[0.055, 8, 8]} />
          <meshStandardMaterial
            color="#ffffff"
            emissive="#ffffff"
            emissiveIntensity={6.0}
          />
        </mesh>

        {/* Línguas de fogo assimétricas dançantes */}
        <mesh ref={labareda1Ref} position={[0.09, 0.24, 0.05]} rotation={[0.1, 0, -0.3]}>
          <coneGeometry args={[0.09, 0.44, 6]} />
          <meshStandardMaterial
            color="#f97316"
            emissive="#c2410c"
            emissiveIntensity={3.2}
            transparent
            opacity={0.86}
          />
        </mesh>

        <mesh ref={labareda2Ref} position={[-0.04, 0.22, 0.09]} rotation={[0.32, 0.3, 0.1]}>
          <coneGeometry args={[0.08, 0.4, 6]} />
          <meshStandardMaterial
            color="#ea580c"
            emissive="#ea580c"
            emissiveIntensity={3.5}
            transparent
            opacity={0.88}
          />
        </mesh>

        <mesh ref={labareda3Ref} position={[-0.08, 0.25, -0.04]} rotation={[-0.15, -0.2, 0.32]}>
          <coneGeometry args={[0.085, 0.42, 6]} />
          <meshStandardMaterial
            color="#dc2626"
            emissive="#b91c1c"
            emissiveIntensity={3.0}
            transparent
            opacity={0.85}
          />
        </mesh>

        <mesh ref={labareda4Ref} position={[0.03, 0.21, -0.08]} rotation={[-0.35, 0.1, -0.1]}>
          <coneGeometry args={[0.075, 0.38, 6]} />
          <meshStandardMaterial
            color="#f97316"
            emissive="#f97316"
            emissiveIntensity={3.4}
            transparent
            opacity={0.88}
          />
        </mesh>
      </group>

      {/* Coluna de Fumaça Volumosa */}
      {comFumaca && (
        <group ref={fumacaGrupoRef} position={[0, 0.1, 0]}>
          {dadosFumaca.map((d) => (
            <mesh key={d.id} position={[0, d.offsetY, 0]}>
              <sphereGeometry args={[d.raio, 7, 7]} />
              <meshStandardMaterial
                color="#1e293b"
                roughness={0.95}
                transparent
                opacity={0.32}
              />
            </mesh>
          ))}
        </group>
      )}

      {/* Brasas Ascendentes */}
      <group ref={brasasGrupoRef} position={[0, 0.12, 0]}>
        {dadosBrasas.map((b) => (
          <mesh key={b.id} position={[0, 0, 0]}>
            <sphereGeometry args={[0.02, 4, 4]} />
            <meshStandardMaterial
              color="#f59e0b"
              emissive="#ea580c"
              emissiveIntensity={5.0}
            />
          </mesh>
        ))}
      </group>
    </group>
  )
}

export interface FogoEFaiscasChoque3DProps {
  posicao?: [number, number, number]
  escala?: number
  ativo?: boolean
}

/**
 * Componente composto mantido para compatibilidade total.
 */
export function FogoEFaiscasChoque3D({
  posicao = [0, 0, 0],
  escala = 1.0,
  ativo = true,
}: FogoEFaiscasChoque3DProps) {
  return (
    <group position={posicao} scale={escala}>
      <Fogo3D ativo={ativo} />
      <FaiscasChoqueEletrico3D ativo={ativo} />
    </group>
  )
}
